import { GameSpecValidationError, exactKeys, integer, record, text } from "./kinds/shared/validators";

export type SourceRendering = "dom" | "canvas-2d" | "webgl";

export interface GameBlueprint {
  readonly version: 1;
  readonly title: string;
  readonly summary: string;
  readonly playerFantasy: string;
  readonly rendering: SourceRendering;
  readonly perspective: string;
  readonly visualDirection: string;
  readonly coreLoop: readonly string[];
  readonly entities: readonly string[];
  readonly systems: readonly string[];
  readonly controls: readonly string[];
  readonly acceptanceCriteria: readonly string[];
}

function stringList(value: unknown, path: string, minCount: number, maxCount: number): readonly string[] {
  if (!Array.isArray(value) || value.length < minCount || value.length > maxCount) {
    throw new GameSpecValidationError(`${path} must contain ${minCount}-${maxCount} entries`);
  }
  return value.map((entry, index) => text(entry, `${path}[${index}]`, 2, 220));
}

export function parseGameBlueprint(value: unknown): GameBlueprint {
  const root = record(value, "game blueprint");
  exactKeys(root, [
    "version",
    "title",
    "summary",
    "playerFantasy",
    "rendering",
    "perspective",
    "visualDirection",
    "coreLoop",
    "entities",
    "systems",
    "controls",
    "acceptanceCriteria",
  ], "game blueprint");
  if (integer(root.version, "game blueprint.version", 1, 1) !== 1) {
    throw new GameSpecValidationError("game blueprint.version must be 1");
  }
  if (root.rendering !== "dom" && root.rendering !== "canvas-2d" && root.rendering !== "webgl") {
    throw new GameSpecValidationError("game blueprint.rendering must be dom, canvas-2d or webgl");
  }
  return {
    version: 1,
    title: text(root.title, "game blueprint.title", 1, 60),
    summary: text(root.summary, "game blueprint.summary", 10, 600),
    playerFantasy: text(root.playerFantasy, "game blueprint.playerFantasy", 5, 300),
    rendering: root.rendering,
    perspective: text(root.perspective, "game blueprint.perspective", 2, 100),
    visualDirection: text(root.visualDirection, "game blueprint.visualDirection", 10, 400),
    coreLoop: stringList(root.coreLoop, "game blueprint.coreLoop", 3, 10),
    entities: stringList(root.entities, "game blueprint.entities", 2, 20),
    systems: stringList(root.systems, "game blueprint.systems", 3, 20),
    controls: stringList(root.controls, "game blueprint.controls", 1, 12),
    acceptanceCriteria: stringList(root.acceptanceCriteria, "game blueprint.acceptanceCriteria", 5, 20),
  };
}
